<script setup>
import { useCampaignGift } from "@/composables/campaign-gift";
import urls from "@/constants/urls";
import { blobToFile, processImageFile } from "@/helpers/processImageFile";
import { notificationsMixin } from "@/mixins";
import axios from "axios";
// import SelectCampaign from "@/views/dashboard/subcomponents/selectCampaign.vue";
import { nanoid } from "nanoid";
import { computed, getCurrentInstance, reactive, ref, watch } from "vue";
import MultiselectSearch from "@/components/multiselect-search.vue";

const emit = defineEmits(["submit"]);

const {
  item,
  loading: loadingCampaignGift,
  fetchOrgCampaignGiftByID,
  createOrgCampaignGift,
  updateOrgCampaignGift,
} = useCampaignGift();

const vm = getCurrentInstance();
const $t = vm.proxy.$t.bind(vm.proxy);
const cId = vm.proxy.$store.state.campaign.campaign.data.id;
const orgId = localStorage.getItem("orgId");
const notificationsMixinHandleCatch =
  notificationsMixin.methods.$_notificationsMixin_handleCatch.bind(vm.proxy);
const notificationsMixinMakeToastCompat =
  notificationsMixin.methods.$_notificationsMixin_makeToastCompat.bind(vm.proxy);

const MODAL_ID = `campaign-gift-modal-${cId}-${nanoid()}`;
const FORM_ID = `campaign-gift-modal-form-${cId}-${nanoid()}`;

const SCOPE_OPTIONS = [
  { value: "all", text: "All" },
  { value: "grandparent", text: "Grandparent" },
  { value: "parent", text: "Parent" },
  { value: "team", text: "Team" },
  { value: "limited", text: "Limited" },
];

const POINTS_INCLUDE_SCOPE_OPTIONS = [
  { value: "all", text: "All" },
  { value: "grandparent", text: "Grandparent" },
  { value: "parent", text: "Parent" },
  { value: "team", text: "Team" },
  { value: "limited", text: "Limited" },
];

const formData = reactive({
  campaign_id: "",
  title: "",
  image: "",
  quantity: "",
  points: "",
  order: 0,
  scope: ["all"],
  scope_team_ids: [],
  points_include_scope: "all",
  points_include_team_ids: [],
});
const imageFile = ref(null);

// selectedTeams holds the multiselect objects; scope_team_ids is the numeric IDs array
const selectedTeams = ref([]);

// selectedPointsIncludeTeams holds the multiselect objects; points_include_team_ids is the numeric IDs array
const selectedPointsIncludeTeams = ref([]);

const teamSearchUrl = computed(() => {
  return urls.apiTeams.replace(":orgId", orgId).replace(":campaignId", cId);
});

const teamSearchOptions = {
  placeholder: "Select teams",
  changeParams: ({ query, page, per_page }) => ({ q: query, page, limit: per_page }),
  customLabel: (e) => `[${e.id}] ${e.name}`,
  processResults: (data) => data.map((el) => ({ id: el.id, ...el.attributes })),
};

watch(selectedTeams, (val) => {
  if (Array.isArray(val)) {
    formData.scope_team_ids = val.map((e) => Number(e.id));
  } else {
    formData.scope_team_ids = [];
  }
});

// When "all" is selected together with other scopes, keep only "all".
// When a specific scope is selected while "all" is active, remove "all".
const prevScope = ref(["all"]);
watch(
  () => formData.scope,
  (val) => {
    if (!Array.isArray(val) || val.length === 0) {
      formData.scope = ["all"];
      prevScope.value = ["all"];
      return;
    }

    const hadAll = prevScope.value.includes("all");
    const hasAll = val.includes("all");

    if (hasAll && val.length > 1) {
      if (!hadAll) {
        // User just added "all" — keep only "all"
        formData.scope = ["all"];
      } else {
        // User added something else while "all" was selected — remove "all"
        formData.scope = val.filter((v) => v !== "all");
      }
    }

    prevScope.value = [...formData.scope];
  }
);

watch(selectedPointsIncludeTeams, (val) => {
  if (Array.isArray(val)) {
    formData.points_include_team_ids = val.map((e) => Number(e.id));
  } else {
    formData.points_include_team_ids = [];
  }
});

async function uploadImageFile(file) {
  const form_data = new FormData();
  const originalName = file.name;
  const type = "image/webp";
  const ext = "webp";

  const webpImageBlob = await processImageFile(file);
  const webpImageFile = blobToFile(webpImageBlob, `${originalName}.${ext}`, type);
  form_data.append("file", webpImageFile);

  const response = await axios.post(urls.loadImageQuill.replace(":OrgId", orgId), form_data);

  return response.data.src;
}

watch(imageFile, (file) => {
  if (file) {
    loadingCampaignGift.value = true;

    uploadImageFile(file)
      .then((src) => {
        formData.image = src;
      })
      .catch((e) => {
        console.error(e);
        notificationsMixinHandleCatch(e);
      })
      .finally(() => {
        loadingCampaignGift.value = false;
      });
  }
});

async function show(id) {
  if (!id) {
    formData.campaign_id = cId;

    vm.proxy.$bvModal.show(MODAL_ID);
    return;
  }

  try {
    await fetchOrgCampaignGiftByID(cId, id, {});

    formData.campaign_id = item.value.campaign_id;
    formData.title = item.value.title;
    formData.image = item.value.image;
    formData.quantity = item.value.quantity;
    formData.points = item.value.points;
    formData.order = item.value.order;
    // Parse scope: supports JSON array (new) or single string (legacy)
    let parsedScope = ["all"];
    if (item.value.scope) {
      try {
        const parsed = JSON.parse(item.value.scope);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsedScope = parsed;
        }
      } catch {
        // Legacy single-value fallback
        parsedScope = [item.value.scope];
      }
    }
    formData.scope = parsedScope;
    prevScope.value = [...parsedScope];

    let parsedIds = [];
    if (item.value.scope_team_ids) {
      try {
        parsedIds = JSON.parse(item.value.scope_team_ids);
      } catch {
        parsedIds = [];
      }
    }
    formData.scope_team_ids = parsedIds;

    // Pre-populate multiselect if there are saved team IDs
    if (parsedIds.length) {
      axios
        .get(teamSearchUrl.value, { params: { team_id: parsedIds } })
        .then(({ data }) => {
          selectedTeams.value = teamSearchOptions.processResults(data.data);
        })
        .catch(() => {});
    } else {
      selectedTeams.value = [];
    }

    formData.points_include_scope = item.value.points_include_scope || "all";

    let parsedPointsIncludeIds = [];
    if (item.value.points_include_team_ids) {
      try {
        parsedPointsIncludeIds = JSON.parse(item.value.points_include_team_ids);
      } catch {
        parsedPointsIncludeIds = [];
      }
    }
    formData.points_include_team_ids = parsedPointsIncludeIds;

    // Pre-populate multiselect if there are saved points include team IDs
    if (parsedPointsIncludeIds.length) {
      axios
        .get(teamSearchUrl.value, { params: { team_id: parsedPointsIncludeIds } })
        .then(({ data }) => {
          selectedPointsIncludeTeams.value = teamSearchOptions.processResults(data.data);
        })
        .catch(() => {});
    } else {
      selectedPointsIncludeTeams.value = [];
    }

    vm.proxy.$bvModal.show(MODAL_ID);
  } catch (e) {
    notificationsMixinHandleCatch(e);
  }
}

async function hide() {
  vm.proxy.$bvModal.hide(MODAL_ID);
}

function clearData() {
  formData.campaign_id = "";
  formData.title = "";
  formData.image = "";
  formData.quantity = "";
  formData.points = "";
  formData.order = 0;
  formData.scope = ["all"];
  formData.scope_team_ids = [];
  selectedTeams.value = [];
  prevScope.value = ["all"];
  formData.points_include_scope = "all";
  formData.points_include_team_ids = [];
  selectedPointsIncludeTeams.value = [];
}

async function submit() {
  const formDataClone = structuredClone(formData);
  formDataClone.campaign_id = Number(formDataClone.campaign_id);
  formDataClone.order = Number(formDataClone.order);
  formDataClone.scope = JSON.stringify(formDataClone.scope || ["all"]);
  formDataClone.scope_team_ids = JSON.stringify(formDataClone.scope_team_ids || []);
  formDataClone.points_include_team_ids = JSON.stringify(
    formDataClone.points_include_team_ids || []
  );

  if (item.value) {
    try {
      await updateOrgCampaignGift(cId, item.value.ID, formDataClone);

      notificationsMixinMakeToastCompat({
        group: "Notification",
        title: $t("edit_campaign.success_save_campaign_gift", "Saved"),
      });
      emit("submit", hide, formDataClone);

      return;
    } catch (e) {
      notificationsMixinHandleCatch(e);
    }
  }

  try {
    await createOrgCampaignGift(cId, formDataClone);

    notificationsMixinMakeToastCompat({
      group: "Notification",
      title: $t("edit_campaign.success_save_campaign_gift", "Saved"),
    });
    emit("submit", hide, formDataClone);

    return;
  } catch (e) {
    notificationsMixinHandleCatch(e);
  }
}
</script>

<template>
  <div>
    <slot :show="show" :loading="loadingCampaignGift" />
    <b-modal :id="MODAL_ID" @hidden="clearData" size="xl" scrollable centered>
      <template #modal-header="{ hide }">
        <div>
          <h2>{{ $t("edit_campaign.campaign_gifts_modal_title", "Gifts") }}</h2>
          <div class="modal-header__hide" @click="hide">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
              />
              <path d="M0 0h24v24H0z" fill="none" />
            </svg>
          </div>
        </div>
      </template>
      <template #modal-footer="{ hide }">
        <div>
          <button type="button" class="bttn bttn--lg bttn--blue" @click="hide" name="button">
            {{ $t("edit_campaign.campaign_gifts_modal_close", "Close") }}
          </button>
          <button
            type="submit"
            :form="FORM_ID"
            :disabled="loadingCampaignGift"
            :class="{
              disabled: loadingCampaignGift,
            }"
            class="bttn bttn--lg bttn--orange"
          >
            {{ $t("edit_campaign.campaign_gifts_modal_save", "Save") }}
          </button>
        </div>
      </template>

      <form :id="FORM_ID" @submit.prevent="submit">
        <!-- no idea if this is needed -->
        <!-- <b-form-group :label="$t('edit_campaign.campaign_gifts_modal_save', 'Campaign ID')">
          <SelectCampaign defaultStyles required v-model="formData.campaign_id" />
        </b-form-group> -->
        <b-form-group :label="$t('edit_campaign.campaign_gifts_modal_form_title', 'Title')">
          <b-form-input required v-model="formData.title" />
        </b-form-group>
        <b-form-group :label="$t('edit_campaign.campaign_gifts_modal_image', 'Image')">
          <b-form-file
            v-model="imageFile"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
          ></b-form-file>
        </b-form-group>
        <b-form-group :label="$t('edit_campaign.campaign_gifts_modal_quantity', 'Quantity')">
          <b-form-input
            required
            type="text"
            inputmode="numeric"
            v-model.number="formData.quantity"
          />
        </b-form-group>
        <b-form-group :label="$t('edit_campaign.campaign_gifts_modal_points', 'Points')">
          <b-form-input required type="text" inputmode="numeric" v-model.number="formData.points" />
        </b-form-group>
        <b-form-group :label="$t('edit_campaign.campaign_gifts_modal_order', 'Order')">
          <b-form-input type="number" inputmode="numeric" v-model.number="formData.order" />
        </b-form-group>
        <b-form-group :label="$t('edit_campaign.campaign_gifts_modal_scope', 'Scope')">
          <b-form-checkbox-group v-model="formData.scope" :options="SCOPE_OPTIONS" stacked />
        </b-form-group>
        <b-form-group
          v-if="formData.scope.includes('limited')"
          :label="$t('edit_campaign.campaign_gifts_modal_scope_teams', 'Scope Teams')"
        >
          <MultiselectSearch
            v-model="selectedTeams"
            :open-prefetch="true"
            :search-url="teamSearchUrl"
            :search-options="teamSearchOptions"
            :multiple="true"
          >
            <template #no-options>
              {{ $t("dashboard.team_search_no_options", "Please enter 1 or more characters") }}
            </template>
            <template #no-more-infinite>
              {{ $t("dashboard.team_search_no_more", "No more teams") }}
            </template>
          </MultiselectSearch>
        </b-form-group>
        <b-form-group
          :label="
            $t('edit_campaign.campaign_gifts_modal_points_include_scope', 'Team Points Include For')
          "
        >
          <b-form-select
            v-model="formData.points_include_scope"
            :options="POINTS_INCLUDE_SCOPE_OPTIONS"
          />
        </b-form-group>
        <b-form-group
          v-if="formData.points_include_scope === 'limited'"
          :label="
            $t(
              'edit_campaign.campaign_gifts_modal_points_include_teams',
              'Team Points Include Teams'
            )
          "
        >
          <MultiselectSearch
            v-model="selectedPointsIncludeTeams"
            :open-prefetch="true"
            :search-url="teamSearchUrl"
            :search-options="teamSearchOptions"
            :multiple="true"
          >
            <template #no-options>
              {{ $t("dashboard.team_search_no_options", "Please enter 1 or more characters") }}
            </template>
            <template #no-more-infinite>
              {{ $t("dashboard.team_search_no_more", "No more teams") }}
            </template>
          </MultiselectSearch>
        </b-form-group>
      </form>
    </b-modal>
  </div>
</template>
